import { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UrgentMessageDisplay from "../../components/UrgentMessageDisplay";
import { WEEKLY_SCHEDULE } from "../../helpers/upcomingServices";
import { getUrl } from "../../helpers/api/api";
import {
  liveStatus_endpoint,
  youtubeServices_endpoint,
  youtubeVideos_endpoint,
  youtubeShorts_endpoint,
} from "../../helpers/api/apiConstants";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";
import {
  EN,
  servicesString,
  worshipWithUsString,
  servicesPageIntroString,
  liveNowLabelString,
  joinUsLiveString,
  latestServiceEyebrowString,
  latestServicePlaceholderString,
  recentServicesString,
  weeklyWorshipEyebrowString,
  moreOnYoutubeEyebrowString,
  moreOnYoutubeBodyString,
  visitYoutubeChannelString,
  watchServiceString,
  recentServicePlaceholderString,
  watchOnYoutubeString,
  sundayWorshipString,
  sundayMorningServiceTimeString,
  sundayEveningServiceTimeString,
  videosSectionString,
  watchMoreServicesOnYoutubeString,
  viewFullScheduleString,
  serviceArchiveLinkString,
  youtubePath,
  vaultPath,
  serviceTimesPath,
} from "../../constants";

const SUNDAY_SERVICES = WEEKLY_SCHEDULE.filter((service) => service.dayOfWeek === 0);

// The 1000px content container fits 3 columns at the grid's 260px minimum
// card width, so 6 keeps Videos to two rows on desktop.
const MAX_VIDEOS_DISPLAYED = 6;

// Shrinks the Recent Services / Videos card grids to 2 columns on mobile
// (they'd otherwise be a single, very long column) and trims their text
// down a notch to match - keeps the page from being all scrolling on
// small screens. Uses !important since these grids/cards are otherwise
// styled with inline styles, which this stylesheet needs to override.
const MOBILE_COMPACT_STYLES = `
  @media (max-width: 640px) {
    .yt-card-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 10px !important;
    }
    .yt-card-body {
      padding: 8px 10px !important;
    }
    .yt-card-title {
      font-size: 12px !important;
    }
    .yt-card-subtext {
      font-size: 10px !important;
      margin: 2px 0 6px !important;
    }
    .yt-card-link {
      font-size: 12px !important;
    }
  }
`;

// The DB only tracks IS_LIVE/LINK, not which specific service is live, so
// this is a best-effort guess from the time of day against the known
// 10am/6pm Sunday schedule - good enough for display copy, not a source
// of truth.
const guessCurrentSundayService = () => {
  const pacificHour = parseInt(
    new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "2-digit",
      hour12: false,
    }),
    10
  );
  return pacificHour < 14 ? SUNDAY_SERVICES[0] : SUNDAY_SERVICES[1];
};

// Turns a stored embed URL (https://www.youtube.com/embed/VIDEO_ID) back
// into a normal watch URL for the "Watch on YouTube" link; falls back to
// the channel link if we don't have anything to work with.
const toWatchUrl = (link) => {
  if (!link) return youtubePath;
  const embedMatch = link.match(/embed\/([\w-]+)/);
  return embedMatch ? `https://www.youtube.com/watch?v=${embedMatch[1]}` : link;
};

const serviceCardTitle = (dayNight, language) =>
  dayNight === "Morning"
    ? sundayMorningServiceTimeString(language)
    : sundayEveningServiceTimeString(language);

// service.date is a plain "YYYY-MM-DD" string - parsing it as local date
// parts (rather than `new Date(dateString)`, which reads it as UTC
// midnight) avoids it displaying as the previous day in western time zones.
const formatServiceDate = (dateStr, language) => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(
    language === EN ? "en-US" : "ro-RO",
    { month: "long", day: "numeric", year: "numeric" }
  );
};

const Services = () => {
  const language = useAppSelector(selectLanguage);
  const theme = useTheme();
  const [isLive, setIsLive] = useState(false);
  const [liveLink, setLiveLink] = useState("");
  // These three come from a new (additive) backend endpoint set - see the
  // separate backend PR. Until that's deployed and configured with a
  // YouTube API key, these fetches just fail quietly and the page falls
  // back to the placeholders below, exactly as it worked before.
  const [ytServices, setYtServices] = useState({ latest: null, recent: [] });
  const [ytVideos, setYtVideos] = useState([]);
  const [ytShorts, setYtShorts] = useState([]);

  useEffect(() => {
    fetch(getUrl(liveStatus_endpoint))
      .then((response) => {
        if (response.ok) return response.json();
        throw response;
      })
      .then((data) => {
        setIsLive(Boolean(data.IS_LIVE));
        setLiveLink(data.LINK || "");
      })
      .catch((error) => {
        console.error("Error getting live status. Error: ", error);
      });

    fetch(getUrl(youtubeServices_endpoint))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => data && setYtServices(data))
      .catch(() => {});

    fetch(getUrl(youtubeVideos_endpoint))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => data && setYtVideos(data))
      .catch(() => {});

    fetch(getUrl(youtubeShorts_endpoint))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => data && setYtShorts(data))
      .catch(() => {});
  }, []);

  const eyebrowStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.brand.brass,
    margin: "0 0 6px",
  };

  const headingStyle = {
    fontFamily: theme.typography.secondaryFontFamily,
    fontWeight: 600,
    color: theme.palette.primary.dark,
    margin: "0 0 12px",
  };

  const bodyStyle = {
    fontFamily: theme.typography.paragraphFontFamily,
    fontSize: "15px",
    lineHeight: 1.6,
    color: theme.palette.text.secondary,
  };

  const primaryLinkStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "14px",
    color: theme.palette.primary.main,
    textDecoration: "none",
  };

  const primaryButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 22px",
    borderRadius: "999px",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.background.paper,
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    fontWeight: 600,
    textDecoration: "none",
  };

  const currentSundayService = guessCurrentSundayService();

  return (
    <div
      style={{
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
        paddingTop: "var(--nav-height)",
        minHeight: "100vh",
      }}
    >
      <style>{MOBILE_COMPACT_STYLES}</style>
      <UrgentMessageDisplay page="Services" handleNoData={() => {}} />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "36px 24px 8px" }}>
        <div style={eyebrowStyle}>{servicesString(language)}</div>
        <h1 style={{ ...headingStyle, fontSize: "min(9vw, 40px)" }}>
          {worshipWithUsString(language)}
        </h1>
        <p style={{ ...bodyStyle, margin: 0, maxWidth: "620px" }}>
          {servicesPageIntroString(language)}
        </p>
      </div>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "28px 24px 0" }}>
        {isLive ? (
          <div>
            <div style={{ ...eyebrowStyle, color: theme.palette.warning.main }}>
              {liveNowLabelString(language)}
            </div>
            <h2 style={{ ...headingStyle, fontSize: "28px" }}>{joinUsLiveString(language)}</h2>
            <div
              style={{
                position: "relative",
                paddingBottom: "56.25%",
                width: "100%",
                backgroundColor: theme.palette.brand.ink,
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <iframe
                src={`${liveLink}?autoplay=1`}
                title="Bethel Bible Church live service"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "15px", color: theme.palette.text.primary }}>
                {currentSundayService.nameString(language)} · {currentSundayService.displayTime}
              </div>
              <a href={toWatchUrl(liveLink)} target="_blank" rel="noreferrer" style={primaryButtonStyle}>
                {watchOnYoutubeString(language)}
                <OpenInNewIcon sx={{ fontSize: "14px" }} />
              </a>
            </div>
          </div>
        ) : (
          <div>
            <div style={eyebrowStyle}>{latestServiceEyebrowString(language)}</div>
            {ytServices.latest ? (
              <div
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  width: "100%",
                  backgroundColor: theme.palette.brand.ink,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${ytServices.latest.videoId}`}
                  title={ytServices.latest.title}
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  aspectRatio: "16 / 9",
                  width: "100%",
                  backgroundColor: theme.palette.brand.linen,
                  border: `1px solid ${theme.palette.brand.border}`,
                  borderRadius: "10px",
                  color: theme.palette.text.secondary,
                }}
              >
                <PlayArrowIcon sx={{ fontSize: "44px", color: theme.palette.brand.brass }} />
                <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "14px", textAlign: "center", maxWidth: "80%" }}>
                  {latestServicePlaceholderString(language)}
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginTop: "16px",
              }}
            >
              {ytServices.latest ? (
                <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "15px", color: theme.palette.text.primary }}>
                  {serviceCardTitle(ytServices.latest.dayNight, language)} · {formatServiceDate(ytServices.latest.date, language)}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "13px",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <span style={{ fontWeight: 700, color: theme.palette.primary.dark }}>{sundayWorshipString(language)}</span>
                  {SUNDAY_SERVICES.map((s) => s.displayTime).join(" · ")}
                </div>
              )}
              <Link to={serviceTimesPath} style={primaryLinkStyle}>
                {viewFullScheduleString(language)} →
              </Link>
            </div>
            <a
              href={ytServices.latest ? ytServices.latest.url : youtubePath}
              target="_blank"
              rel="noreferrer"
              style={{ ...primaryButtonStyle, marginTop: "16px" }}
            >
              {ytServices.latest ? watchServiceString(language) : watchOnYoutubeString(language)}
              <OpenInNewIcon sx={{ fontSize: "14px" }} />
            </a>
          </div>
        )}
      </div>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 0" }}>
        <div style={eyebrowStyle}>{recentServicesString(language)}</div>
        <div
          className="yt-card-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "20px",
            marginTop: "12px",
          }}
        >
          {ytServices.recent.length > 0
            ? ytServices.recent.map((service) => (
                <a
                  key={service.videoId}
                  href={service.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    border: `1px solid ${theme.palette.brand.border}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.background.paper,
                    textDecoration: "none",
                    display: "block",
                  }}
                >
                  <div
                    style={{
                      aspectRatio: "16 / 9",
                      backgroundColor: theme.palette.brand.linen,
                      backgroundImage: `url(${service.thumbnail})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                  <div className="yt-card-body" style={{ padding: "14px 16px" }}>
                    <div
                      className="yt-card-title"
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "15px",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {serviceCardTitle(service.dayNight, language)}
                    </div>
                    <div
                      className="yt-card-subtext"
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "12px",
                        color: theme.palette.text.secondary,
                        margin: "2px 0 10px",
                      }}
                    >
                      {formatServiceDate(service.date, language)}
                    </div>
                    <span className="yt-card-link" style={primaryLinkStyle}>
                      {watchServiceString(language)} →
                    </span>
                  </div>
                </a>
              ))
            : Array.from({ length: 6 }, (_, index) => {
                const service = SUNDAY_SERVICES[index % SUNDAY_SERVICES.length];
                return (
                  <div
                    key={index}
                    style={{
                      border: `1px solid ${theme.palette.brand.border}`,
                      borderRadius: "10px",
                      overflow: "hidden",
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        aspectRatio: "16 / 9",
                        backgroundColor: theme.palette.brand.linen,
                        color: theme.palette.brand.brass,
                      }}
                    >
                      <OndemandVideoIcon sx={{ fontSize: "32px" }} />
                    </div>
                    <div className="yt-card-body" style={{ padding: "14px 16px" }}>
                      <div
                        className="yt-card-title"
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: "15px",
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {service.nameString(language)}
                      </div>
                      <div
                        className="yt-card-subtext"
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: "12px",
                          color: theme.palette.text.secondary,
                          margin: "2px 0 10px",
                        }}
                      >
                        {recentServicePlaceholderString(language)}
                      </div>
                      <span className="yt-card-link" style={{ ...primaryLinkStyle, opacity: 0.5, cursor: "default" }}>
                        {watchServiceString(language)} →
                      </span>
                    </div>
                  </div>
                );
              })}
        </div>
        <a
          href={youtubePath}
          target="_blank"
          rel="noreferrer"
          style={{ ...primaryLinkStyle, marginTop: "16px" }}
        >
          {watchMoreServicesOnYoutubeString(language)} →
        </a>
      </div>

      {ytVideos.length > 0 && (
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 0" }}>
          <div style={eyebrowStyle}>{videosSectionString(language)}</div>
          <div
            className="yt-card-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "20px",
              marginTop: "12px",
            }}
          >
            {ytVideos.slice(0, MAX_VIDEOS_DISPLAYED).map((video) => (
              <a
                key={video.videoId}
                href={video.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  border: `1px solid ${theme.palette.brand.border}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.background.paper,
                  textDecoration: "none",
                  display: "block",
                }}
              >
                <div
                  style={{
                    aspectRatio: "16 / 9",
                    backgroundColor: theme.palette.brand.linen,
                    backgroundImage: `url(${video.thumbnail})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="yt-card-body" style={{ padding: "14px 16px" }}>
                  <div
                    className="yt-card-title"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {video.title}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {ytShorts.length > 0 && (
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 0" }}>
          <div style={eyebrowStyle}>Shorts</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: "16px",
              marginTop: "12px",
            }}
          >
            {ytShorts.map((short) => (
              <a
                key={short.videoId}
                href={short.url}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none", display: "block" }}
              >
                <div
                  style={{
                    aspectRatio: "9 / 16",
                    borderRadius: "10px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.brand.ink,
                    backgroundImage: `url(${short.thumbnail})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginTop: "8px",
                  }}
                >
                  {short.title}
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 0" }}>
        <div style={eyebrowStyle}>{weeklyWorshipEyebrowString(language)}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "32px", marginTop: "8px" }}>
          {SUNDAY_SERVICES.map((service) => (
            <div key={`${service.dayOfWeek}-${service.hour}`}>
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: theme.palette.text.secondary,
                }}
              >
                {service.nameString(language)}
              </div>
              <div
                style={{
                  fontFamily: theme.typography.secondaryFontFamily,
                  fontSize: "24px",
                  fontWeight: 600,
                  color: theme.palette.primary.dark,
                }}
              >
                {service.displayTime}
              </div>
            </div>
          ))}
        </div>
        <Link to={serviceTimesPath} style={{ ...primaryLinkStyle, marginTop: "16px" }}>
          {viewFullScheduleString(language)} →
        </Link>
      </div>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "48px 24px 40px" }}>
        <div style={eyebrowStyle}>{moreOnYoutubeEyebrowString(language)}</div>
        <p style={{ ...bodyStyle, margin: "0 0 16px", maxWidth: "560px" }}>
          {moreOnYoutubeBodyString(language)}
        </p>
        <a href={youtubePath} target="_blank" rel="noreferrer" style={primaryButtonStyle}>
          {visitYoutubeChannelString(language)}
          <OpenInNewIcon sx={{ fontSize: "14px" }} />
        </a>
      </div>

      {/* Small, deliberately inconspicuous link to the old detailed
          per-service archive - see vault.js */}
      <div style={{ textAlign: "center", padding: "8px 24px 32px" }}>
        <Link
          to={vaultPath}
          style={{
            fontFamily: theme.typography.fontFamily,
            fontSize: "11px",
            color: theme.palette.text.secondary,
            opacity: 0.7,
            textDecoration: "none",
          }}
        >
          {serviceArchiveLinkString(language)}
        </Link>
      </div>
    </div>
  );
};

export default Services;
